/**
 * Favorite filters (SAU-260): pure types + fingerprint logic shared by the
 * RuleRow star button and the "Add Fav" menu. No API imports — everything in
 * this file is trivially unit-testable.
 *
 * The fingerprint (`favoriteRuleSignature`) is the single source of truth for
 * both "should this star render filled?" and "is this a duplicate?". The
 * exact string is also POSTed to the backend as `signature`, where a unique
 * index on its hash makes duplicates impossible — so the normalization here
 * must stay stable (changing it orphans previously stored signatures).
 */

import type { FavoriteFilter as FavoriteFilterModel } from '@/ts';
import { hasBothRangeEnds, isCompleteRange } from '@/utils/filterUtils';

/** Snapshot of one filter row: column + operator + value, minus the row id. */
export interface FavoriteRuleSnapshot {
  column: string;
  field: string | null;
  criterion: string;
  value: any;
}

/**
 * A favorite as the API resource returns it (`GET /api/favorite_filter`):
 * id, entity, label, rule, signature, created_at.
 *
 * Derived from the generated backend model interface so the two can't
 * silently diverge, with the wire-shape corrections the generated types
 * can't express:
 * - `rule` is a {@link FavoriteRuleSnapshot}, not the generated `any[]`.
 * - `signature` is optional only for the deploy window where the live
 *   backend predates the field — matching falls back to computing it from
 *   `rule` (see useFavoriteFilters).
 * - `created_at` arrives as an ISO string over JSON (the generated `Date`
 *   type is never revived on the frontend).
 */
export type FavoriteFilter = Pick<FavoriteFilterModel, 'id' | 'entity' | 'label'> &
  Partial<Pick<FavoriteFilterModel, 'signature'>> & {
    rule: FavoriteRuleSnapshot;
    created_at: string;
  };

/**
 * The dotted TableColumn key a rule binds to: `column.field` when the rule
 * targets a filter-only sub-field, else just `column`. The single owner of
 * that join — RuleRow's column binding and the favorites menu's stale-column
 * guard both resolve through it. Empty-string field counts as no field,
 * mirroring `favoriteRuleSignature`.
 */
export function favoriteRuleColumnKey(
  rule: Pick<FavoriteRuleSnapshot, 'column' | 'field'>,
): string {
  return rule.field ? `${rule.column}.${rule.field}` : rule.column;
}

/** Max number of value labels spelled out in a favorite's label before "+N more". */
export const FAVORITE_LABEL_VALUE_CAP = 3;

/**
 * Hard cap on the whole label — the backend validates `label` as `max:255`
 * (FavoriteFilterRequest), so anything longer would 422 on EVERY star attempt
 * and make the row permanently un-starrable.
 */
export const FAVORITE_LABEL_MAX_LENGTH = 255;

/**
 * Normalizes a rule value so shapes the filter UI itself treats as identical
 * fingerprint identically (each coercion mirrors one RuleRow already performs):
 *
 * - `null` / `undefined` / `""` all mean "no value" → `null` (matches the
 *   "is empty" operators, which force the value to null).
 * - Booleans stay booleans — real `true` is NOT the string `"true"`.
 * - Arrays are `String()`ed, de-duplicated, and sorted — `["a","b"]` equals
 *   `["b","a"]`; an emptied array counts as "no value".
 * - Any other scalar unifies with its one-element array (`"5"` ≡ `["5"]` ≡ 5,
 *   because the combobox controls collapse one-item arrays to scalars).
 */
export function normalizeFavoriteValue(value: unknown): null | boolean | string[] {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) {
    const normalized = [...new Set(value.map(String))].sort();
    return normalized.length === 0 ? null : normalized;
  }
  return [String(value)];
}

/**
 * Stable fingerprint of a rule — same string means same favorite. This exact
 * string is sent to the backend as `signature` on create.
 */
export function favoriteRuleSignature(
  rule: Pick<FavoriteRuleSnapshot, 'column' | 'field' | 'criterion' | 'value'>,
): string {
  return JSON.stringify({
    column: rule.column,
    // Empty-string field counts the same as null field.
    field: rule.field || null,
    criterion: rule.criterion,
    // A range's two ends are a from and a to, so their order is part of the rule. Every
    // other array is a set of values, which the normalization sorts.
    value:
      rule.criterion === 'between' && hasBothRangeEnds(rule.value)
        ? [...rule.value]
        : normalizeFavoriteValue(rule.value),
  });
}

/**
 * Only complete rows can be starred: column and operator picked, and a value
 * present — except valueless operators ("is empty" / "is not empty", i.e.
 * `criterionControl === 'none'`), which are legitimately complete.
 */
export function isFavoritableRule(
  rule: Pick<FavoriteRuleSnapshot, 'column' | 'criterion' | 'value'>,
  criterionControl?: string | null,
): boolean {
  if (!rule.column || !rule.criterion) {
    return false;
  }
  if (criterionControl === 'none') {
    return true;
  }
  if (criterionControl === 'date_range') {
    return isCompleteRange(rule.value);
  }
  return normalizeFavoriteValue(rule.value) !== null;
}

const rangeDateFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** "Nov 1, 2026" for `2026-11-01`. The date is read as a calendar day, so no time zone moves it. */
function formatRangeEnd(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return isoDate;
  return rangeDateFormat.format(new Date(year, month - 1, day));
}

/**
 * The value part of a "between" rule's label: "Nov 1, 2026 and Nov 7, 2026". An end that
 * is not picked yet reads as "…".
 */
export function dateRangeValueLabel(value: unknown): string {
  const ends = Array.isArray(value) ? value : [];
  const [from, to] = [ends[0], ends[1]].map((end) =>
    typeof end === 'string' && end !== '' ? formatRangeEnd(end) : '…',
  );
  return `${from} and ${to}`;
}

/**
 * Plain-English label snapshot taken at the moment a row is starred:
 * column header + lowercased operator label + value labels, e.g.
 * "Online is True", "Used By Account is one of Acme, Globex", "Genre is empty".
 * Long value lists cap at {@link FAVORITE_LABEL_VALUE_CAP} names + "+N more".
 */
export function buildFavoriteLabel(input: {
  columnHeader: string;
  criterionLabel: string;
  valueLabels: string[];
}): string {
  const parts = [input.columnHeader, input.criterionLabel.toLowerCase()];
  const valueLabels = input.valueLabels.filter((valueLabel) => valueLabel !== '');
  if (valueLabels.length > 0) {
    const shown = valueLabels.slice(0, FAVORITE_LABEL_VALUE_CAP).join(', ');
    const hiddenCount = valueLabels.length - FAVORITE_LABEL_VALUE_CAP;
    parts.push(hiddenCount > 0 ? `${shown} +${hiddenCount} more` : shown);
  }
  const label = parts.join(' ');
  // LAST step, so no input (long typed value, long option names, long column
  // header) can ever exceed the backend's max:255 — see FAVORITE_LABEL_MAX_LENGTH.
  if (label.length > FAVORITE_LABEL_MAX_LENGTH) {
    return `${label.slice(0, FAVORITE_LABEL_MAX_LENGTH - 1)}…`;
  }
  return label;
}
